import { api } from "./dataService";

/**
 * Two-Factor Authentication Service
 * Handles all 2FA-related API calls
 */

/**
 * Request a 2FA challenge for the user
 * @param {string} email - User email
 * @param {string} provider_type - Type of 2FA provider (e.g., 'email')
 * @param {string} challenge_token - Challenge token from login response
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<Object>} Challenge response data
 */
export const requestTwoFactorChallenge = async (
  email,
  provider_type = "email",
  challenge_token,
  callbacks = {}
) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.post(
      "auth/2fa/request-challenge",
      {
        provider_type,
      },
      {
        headers: {
          Authorization: `Bearer ${challenge_token}`,
        },
      }
    );

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    }

    return response.data.data;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

/**
 * Verify a 2FA code
 * @param {string} challenge_id - Challenge ID from the challenge request
 * @param {string} code - Verification code entered by user
 * @param {string} challenge_token - Challenge token from login response
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<Object>} Verification response data
 */
export const verifyTwoFactorCode = async (
  challenge_id,
  code,
  challenge_token,
  callbacks = {}
) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.post(
      "auth/2fa/verify",
      {
        code,
        provider_type: "email",
      },
      {
        headers: {
          Authorization: `Bearer ${challenge_token}`,
        },
      }
    );

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    }

    return response.data.data;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

/**
 * Get 2FA status for authenticated user
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<Object>} 2FA status data
 */
export const getTwoFactorStatus = async (callbacks = {}) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.get("auth/2fa/status");

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    }

    return response.data.data;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

/**
 * Setup 2FA for authenticated user
 * @param {string} provider_type - Type of 2FA provider
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<Object>} Setup response data
 */
export const setupTwoFactor = async (
  provider_type = "email",
  callbacks = {}
) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.post("auth/2fa/setup", {
      provider_type,
    });

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    }

    return response.data.data;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

/**
 * Verify setup of 2FA provider
 * @param {string} provider_id - Provider ID from setup
 * @param {string} code - Verification code
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<Object>} Verification response data
 */
export const verifyTwoFactorSetup = async (
  provider_id,
  code,
  callbacks = {}
) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.post("auth/2fa/verify-setup", {
      provider_id,
      code,
    });

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    }

    return response.data.data;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};
